/**
 * Pattern-matched handlers for the mock network layer.
 *
 * Submissions and brokerages now hit the real backend (/v2/* and
 * /v2/brokerages/*, /v2/brokers/*). What's left here:
 *   - /applications/:id/litigation — heatmap data
 *   - /v1/auth/forgot-password — placeholder until BE ships it
 *   - /v2/rates — actuarial rate table for Schedule live pricing
 *   - /v2/relativity — default relativity factors for the Schedule row
 *   - /v2/sir-credits — SIR retention → credit tiers
 *   - /v2/submissions/:id/facilities — SoT for Schedule of Location +
 *     Facility Schedule (localStorage-backed; see facilities-store.ts)
 *
 * Loss Runs is NOT here — it hits the real backend (`/v2/.../loss-runs/`
 * + `/summary`) directly via `endpoints/loss-runs.ts` (0% mock).
 * Operational (staffing ratios) is NOT here — it hits the real backend
 * (`/v2/staffing-ratios/`) directly via `endpoints/operational.ts`.
 */
import type { FacilityWritePayload } from "../types/facility";
import {
    createFacility as facilitiesCreate,
    deleteFacility as facilitiesDelete,
    listFacilities as facilitiesList,
    patchFacility as facilitiesPatch,
} from "../mocks/facilities-store";
import ratesFixture from "../mocks/data/rates.json";
import relativityFixture from "../mocks/data/relativity.json";
import sirCreditsFixture from "../mocks/data/sir-credits.json";
import { listLitigation } from "../mocks/store";
import { withDelay } from "./client";

type Params = Record<string, string>;
type Query = Record<string, string>;

type Handler = (ctx: { params: Params; query: Query; body: unknown }) => Promise<Response> | Response;

const GET_LATENCY = 600;
const WRITE_LATENCY = 800;

interface Route {
    method: string;
    pattern: string;
    handler: Handler;
}

const routes: Route[] = [
    {
        method: "GET",
        pattern: "/applications/:id/litigation",
        handler: async ({ params, query }) => {
            const years = Number(query.years ?? 5);
            const data = listLitigation(params.id, Number.isFinite(years) ? years : 5);
            return json(await withDelay(GET_LATENCY, data));
        },
    },
    {
        method: "POST",
        pattern: "/v1/auth/forgot-password",
        handler: async () => json(await withDelay(WRITE_LATENCY, { status: "ok" })),
    },
    {
        // Wraps the same fixture used by `domain/pricing` tests so a UI
        // hitting the mock layer sees the exact same numbers a unit
        // test asserts on — no drift between the two.
        method: "GET",
        pattern: "/v2/rates",
        handler: async () => json(await withDelay(GET_LATENCY, { rates: ratesFixture })),
    },
    {
        // Default relativity factors handed to the Schedule's Relativity
        // row whenever a submission's slot is empty. Shared with the
        // domain/pricing `RELATIVITY` constants — both ultimately point
        // at the same UW baseline (Skilled 1.0, Assisted 0.35,
        // Independent 0.15, Addl Exposure 1.0).
        method: "GET",
        pattern: "/v2/relativity",
        handler: async () => json(await withDelay(GET_LATENCY, relativityFixture)),
    },
    {
        // SIR (Self-Insured Retention) credit tiers. Same fixture the
        // domain/pricing tests import — no drift between unit tests and
        // the UI's live calc.
        method: "GET",
        pattern: "/v2/sir-credits",
        handler: async () => json(await withDelay(GET_LATENCY, { tiers: sirCreditsFixture })),
    },
    // ── Facilities SoT (localStorage-backed) ─────────────────────
    // Latency for mutations is dropped (50 ms) so live-editing in the
    // table feels instant; the GET keeps the 600 ms so the loading
    // state is observable for skeleton/UX work.
    {
        method: "GET",
        pattern: "/v2/submissions/:submissionId/facilities",
        handler: async ({ params }) =>
            json(await withDelay(GET_LATENCY, facilitiesList(params.submissionId))),
    },
    {
        method: "POST",
        pattern: "/v2/submissions/:submissionId/facilities",
        handler: async ({ params, body }) =>
            json(await withDelay(50, facilitiesCreate(params.submissionId, (body ?? {}) as FacilityWritePayload))),
    },
    {
        method: "PATCH",
        pattern: "/v2/submissions/:submissionId/facilities/:facilityId",
        handler: async ({ params, body }) => {
            const result = facilitiesPatch(
                params.submissionId,
                params.facilityId,
                (body ?? {}) as FacilityWritePayload,
            );
            if (!result) return notFound();
            return json(await withDelay(50, result));
        },
    },
    {
        method: "DELETE",
        pattern: "/v2/submissions/:submissionId/facilities/:facilityId",
        handler: async ({ params }) => {
            const ok = facilitiesDelete(params.submissionId, params.facilityId);
            if (!ok) return notFound();
            await withDelay(50, null);
            return new Response(null, { status: 204 });
        },
    },
];

export async function resolveMockRequest(input: RequestInfo, init?: RequestInit): Promise<Response> {
    const method = (init?.method ?? "GET").toUpperCase();
    const urlString = typeof input === "string" ? input : input.url;
    const url = new URL(urlString, "http://mock.local");
    const pathname = url.pathname;

    for (const route of routes) {
        if (route.method !== method) continue;
        const params = matchPath(route.pattern, pathname);
        if (!params) continue;

        const query: Query = {};
        url.searchParams.forEach((value, key) => {
            query[key] = value;
        });

        const body = init?.body ? JSON.parse(init.body.toString()) : undefined;
        return route.handler({ params, query, body });
    }

    return notFound();
}

function matchPath(pattern: string, pathname: string): Params | null {
    const patternParts = pattern.split("/").filter(Boolean);
    const pathParts = pathname.split("/").filter(Boolean);
    if (patternParts.length !== pathParts.length) return null;

    const params: Params = {};
    for (let i = 0; i < patternParts.length; i++) {
        const pp = patternParts[i];
        const sp = pathParts[i];
        if (pp.startsWith(":")) {
            params[pp.slice(1)] = decodeURIComponent(sp);
        } else if (pp !== sp) {
            return null;
        }
    }
    return params;
}

function json(data: unknown, status = 200): Response {
    return new Response(JSON.stringify(data), {
        status,
        headers: { "Content-Type": "application/json" },
    });
}

function notFound(): Response {
    return new Response(JSON.stringify({ error: "Not found" }), {
        status: 404,
        headers: { "Content-Type": "application/json" },
    });
}
